import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, Variants } from "framer-motion";
import {
  Moon,
  Sun,
  Lock,
  User,
  ArrowRight,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PATHS } from "./config/paths";

interface LoginPayload {
  username: string;
  password: string;
}

interface LoginPageProps {
  onLogin: (payload: LoginPayload) => Promise<void>;
}

// Canvas Particle Network Component
const ParticleNetwork = ({ isDark }: { isDark: boolean }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let particles: any[] = [];
    const particleCount = 120;
    const connectionDistance = 150;
    const particleSpeed = 0.8;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resize);
    resize();

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;

      constructor() {
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.vx = (Math.random() - 0.5) * particleSpeed;
        this.vy = (Math.random() - 0.5) * particleSpeed;
        this.radius = Math.random() * 1.5 + 0.5;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas!.width) this.vx = -this.vx;
        if (this.y < 0 || this.y > canvas!.height) this.vy = -this.vy;
      }

      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? "rgba(100, 150, 255, 0.4)" : "rgba(59, 130, 246, 0.3)";
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();

        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            let opacity = 1 - distance / connectionDistance;
            ctx.strokeStyle = isDark
              ? `rgba(100, 150, 255, ${opacity * 0.15})`
              : `rgba(59, 130, 246, ${opacity * 0.2})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDark]);

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />;
};

export function LoginPage({ onLogin }: LoginPageProps) {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved === "dark"; // default to light unless explicitly saved as dark
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "error" | "success">("idle");
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [btnPos, setBtnPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  const handleMouseMove = (e: React.MouseEvent) => {
    setMouse({ x: e.clientX, y: e.clientY });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 2000);
      return;
    }

    setStatus("loading");
    try {
      await onLogin({ username: username.trim(), password: password.trim() });
      setStatus("success");
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 2000);
    }
  };

  const handleMagnet = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) * 0.15;
    const y = (e.clientY - rect.top - rect.height / 2) * 0.15;
    setBtnPos({ x, y });
  };

  const resetMagnet = () => setBtnPos({ x: 0, y: 0 });

  const variants: Variants = {
    error: {
      x: [0, -8, 8, -6, 6, 0],
      transition: { duration: 0.4 },
    },
    success: {
      scale: 0.88,
      opacity: 0,
      filter: "blur(20px)",
      transition: {
        duration: 1.2,
        ease: [0.34, 1.56, 0.64, 1]
      },
    },
    idle: { scale: 1, opacity: 1 },
    loading: { scale: 1, opacity: 1 },
  };

  return (
    <div
      onMouseMove={handleMouseMove}
      className={`relative min-h-screen flex items-center justify-center overflow-hidden transition-colors duration-700 ${isDark ? "bg-[#05070F] text-white" : "bg-gradient-to-br from-slate-100 via-white to-slate-200 text-slate-900"
        }`}
    >
      {/* 🌌 Spotlight */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background: isDark
            ? `radial-gradient(700px circle at ${mouse.x}px ${mouse.y}px, rgba(59,130,246,0.15), transparent 45%)`
            : `radial-gradient(700px circle at ${mouse.x}px ${mouse.y}px, rgba(59,130,246,0.12), transparent 45%)`,
        }}
      />

      <ParticleNetwork isDark={isDark} />

      {/* 🌑 / 🌤 Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isDark
            ? "radial-gradient(circle, transparent, rgba(0,0,0,0.65))"
            : "radial-gradient(circle, transparent, rgba(148,163,184,0.25))",
        }}
      />

      {/* Theme Toggle */}
      <button
        onClick={() => {
          const newDark = !isDark;
          setIsDark(newDark);
          localStorage.setItem("theme", newDark ? "dark" : "light");
        }}
        className={`absolute top-8 right-8 z-50 p-3 rounded-full backdrop-blur-xl border transition-all duration-300 hover:scale-110 active:scale-95 ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-black/5 border-black/10 text-slate-600"
          }`}
      >
        {isDark ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* 💥 SUCCESS PORTAL */}
      <AnimatePresence>
        {status === "success" && (
          <motion.div
            initial={{ scale: 0.1, opacity: 0 }}
            animate={{ scale: 30, opacity: [0, 0.5, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 m-auto w-40 h-40 bg-blue-500/30 rounded-full blur-[120px] pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* 🧊 CARD */}
      <motion.div
        variants={variants}
        animate={status}
        className={`relative z-10 w-full max-w-md p-10 rounded-[2.5rem] overflow-hidden transition-all duration-500 ${isDark
          ? "backdrop-blur-3xl border border-white/10 bg-[#0a0f1e]/80 shadow-[0_0_80px_rgba(0,0,0,0.7)]"
          : "bg-white/95 border border-slate-200 shadow-2xl"
          }`}
      >
        {/* ✨ Glass reflection */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-20 pointer-events-none" />

        {/* Header */}
        <div className="flex flex-col items-center mb-10">
          <motion.div
            animate={status === "success" ? { rotate: 360 } : {}}
            transition={{ duration: 0.8 }}
            className={`w-18 h-18 flex items-center justify-center rounded-3xl shadow-2xl transition-colors duration-500 ${status === "success" ? "bg-emerald-500 shadow-emerald-500/20" : "bg-blue-600"
              }`}
          >
            {status === "success" ? (
              <CheckCircle2 className="text-white" size={34} />
            ) : (
              <ShieldCheck className="text-white" size={34} />
            )}
          </motion.div>

          <div className="mt-8 mb-2">
            <img src="/logo12.png" alt="XeroAlpha" className="h-12 w-auto drop-shadow-2xl" />
          </div>

          <p className={`text-[10px] tracking-[0.4em] font-bold uppercase mt-2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
            Secure Access Node
          </p>
          <div className={`h-1 w-12 rounded-full mt-6 bg-blue-600/50 shadow-[0_0_10px_rgba(37,99,235,0.5)]`} />
        </div>

        {/* FORM */}
        <form onSubmit={handleLogin} className="space-y-6">
          <div className="relative group">
            <User className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-gray-500 group-focus-within:text-blue-400' : 'text-slate-400 group-focus-within:text-blue-600'}`} size={18} />
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username/Email"
              className={`w-full pl-12 pr-4 py-4 rounded-2xl transition-all font-medium tracking-tight ${isDark
                ? 'bg-black/40 border border-white/5 text-white placeholder:text-gray-600 focus:ring-2 focus:ring-blue-500/50 outline-none'
                : 'bg-slate-100 border border-slate-200 text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/50 outline-none shadow-inner'
                }`}
            />
          </div>

          <div className="space-y-3">
            <div className="relative group">
              <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-gray-500 group-focus-within:text-blue-400' : 'text-slate-400 group-focus-within:text-blue-600'}`} size={18} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className={`w-full pl-12 pr-12 py-4 rounded-2xl transition-all font-medium tracking-tight ${isDark
                  ? 'bg-black/40 border border-white/5 text-white placeholder:text-gray-600 focus:ring-2 focus:ring-blue-500/50 outline-none'
                  : 'bg-slate-100 border border-slate-200 text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/50 outline-none shadow-inner'
                  }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-400 hover:text-slate-900'}`}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="hidden justify-end pr-1">
              <button
                type="button"
                onClick={() => navigate(PATHS.FORGOT_PASSWORD)}
                className={`text-[10px] font-bold uppercase tracking-widest hover:underline transition-all ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
              >
                Forgot Access Key?
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {status === "error" && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold uppercase tracking-wider"
              >
                <AlertCircle size={14} /> Invalid credentials
              </motion.div>
            )}
          </AnimatePresence>

          {/* 🧲 MAGNET BUTTON */}
          <motion.button
            onMouseMove={handleMagnet}
            onMouseLeave={resetMagnet}
            style={{
              x: btnPos.x,
              y: btnPos.y,
            }}
            type="submit"
            disabled={status === "loading" || status === "success"}
            className={`w-full py-4 rounded-2xl font-black text-xs tracking-[0.2em] uppercase flex justify-center items-center gap-3 transition-all duration-300 ${status === 'success'
              ? 'bg-emerald-500 text-white shadow-[0_0_30px_rgba(16,185,129,0.4)]'
              : isDark
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-500 hover:to-indigo-500 shadow-[0_10px_30px_rgba(37,99,235,0.4)] hover:scale-[1.02]'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-500 hover:to-indigo-500 shadow-[0_10px_30px_rgba(37,99,235,0.2)]'
              } ${status === 'loading' ? 'cursor-wait opacity-90' : 'cursor-pointer'}`}
          >
            {status === "loading" ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
              />
            ) : status === "success" ? (
              "Access Granted"
            ) : (
              <>
                Authenticate <ArrowRight size={18} />
              </>
            )}
          </motion.button>
        </form>

        <footer className="mt-12 text-center">
          <p className={`text-[10px] font-bold uppercase tracking-[0.4em] ${isDark ? "text-slate-600" : "text-slate-400"}`}>
            Protected by XeroAlpha Security
          </p>
        </footer>
      </motion.div>
    </div>
  );
}